import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const ACTION_COLORS = {
  USER: '#3b82f6',
  PROJECT: '#8b5cf6',
  PAGE: '#f59e0b',
  PLANNING: '#10b981',
  ASSEMBLY: '#ec4899',
  RESOURCE: '#06b6d4',
};

const getActionColor = (action) => {
  const key = Object.keys(ACTION_COLORS).find(k => action?.includes(k));
  return key ? ACTION_COLORS[key] : 'var(--pico-muted-color)';
};

const formatRelativeTime = (ts) => {
  if (!ts) return '';
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const ActivityLog = ({ logs = [], users = [] }) => {
  const { t } = useTranslation();
  const [filterOrg, setFilterOrg] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Derive filter options
  const orgOptions = useMemo(() => {
    const orgs = new Set(logs.map(l => l.organization).filter(Boolean));
    return Array.from(orgs).sort();
  }, [logs]);

  const actionOptions = useMemo(() => {
    const actions = new Set(logs.map(l => l.action).filter(Boolean));
    return Array.from(actions).sort();
  }, [logs]);

  // Build user email lookup for enrichment
  const emailMap = useMemo(() => {
    const map = {};
    users.forEach(u => { if (u.uid && u.email) map[u.uid] = u.email; });
    return map;
  }, [users]);

  // Filtered logs
  const filtered = useMemo(() => {
    return logs.filter(log => {
      if (filterOrg !== 'all' && log.organization !== filterOrg) return false;
      if (filterAction !== 'all' && log.action !== filterAction) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const haystack = [
          log.action, log.organization, log.actorEmail,
          emailMap[log.actorUid], log.details?.projectName,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [logs, filterOrg, filterAction, searchQuery, emailMap]);

  // Group by date
  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach(log => {
      const date = log.timestamp
        ? new Date(log.timestamp).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })
        : 'Unknown';
      if (!groups[date]) groups[date] = [];
      groups[date].push(log);
    });
    return groups;
  }, [filtered]);

  return (
    <div className="activity-tab-content">
      {/* Filters bar */}
      <div className="activity-filters">
        <input
          type="search"
          placeholder={t('admin.search_placeholder')}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="activity-search"
        />
        <select value={filterOrg} onChange={e => setFilterOrg(e.target.value)} className="activity-filter-select">
          <option value="all">{t('admin.stats.active_roles')}</option>
          {orgOptions.map(org => <option key={org} value={org}>{org}</option>)}
        </select>
        <select value={filterAction} onChange={e => setFilterAction(e.target.value)} className="activity-filter-select">
          <option value="all">All Actions</option>
          {actionOptions.map(a => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
        </select>
        <span className="activity-count">{filtered.length} events</span>
      </div>

      {/* Timeline */}
      {Object.keys(grouped).length === 0 ? (
        <article className="glass empty-activity">
          <p>{t('messages.no_messages')}</p>
        </article>
      ) : (
        <div className="activity-timeline">
          {Object.entries(grouped).map(([date, entries]) => (
            <div key={date} className="timeline-group">
              <div className="timeline-date-label">{date}</div>
              {entries.map(log => (
                <div key={log.id} className="timeline-entry">
                  <div className="timeline-line">
                    <span className="timeline-dot" style={{ background: getActionColor(log.action) }} />
                  </div>
                  <div className="timeline-card">
                    <div className="timeline-card-header">
                      <div className="timeline-header-left">
                        <span className="timeline-action" style={{ color: getActionColor(log.action) }}>
                          {log.action?.replace(/_/g, ' ')}
                        </span>
                        <span className="timeline-org-badge">{log.organization}</span>
                      </div>
                      <span className="timeline-time">{formatRelativeTime(log.timestamp)}</span>
                    </div>
                    <div className="timeline-card-body">
                      <span className="timeline-actor">
                        {log.actorEmail || emailMap[log.actorUid] || 'System'}
                      </span>
                      {log.details?.projectName && (
                        <span className="timeline-project">{log.details.projectName}</span>
                      )}
                      {log.details?.version && (
                        <span className="timeline-detail">v: {log.details.version}</span>
                      )}
                      {log.details?.page !== undefined && (
                        <span className="timeline-detail">p. {log.details.page}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityLog;
